// =======================================================================================================
import { Component, ReactNode, ReactElement, CSSProperties, MouseEvent } from 'react';
import { Property } from 'csstype';
import _ from "lodash";
import Tippy from "@tippyjs/react";
import { IOutline, ITsViewFormat } from '@ModelViews/ModelViews';
import { CanvasItemLayoutDesc, CanvasItemDesc } from "./Structs";
import { getIconClassName } from "@Primitive/IconSelectorUtils";
import { ExDialog } from "@Primitive/ExDialog";
import { isDefined, getRandomSmallKey, isStringEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { getZDepthShadow } from '@Utils/Colors';
import { __LAST_UPDATE_TS_KEY__ } from '@UI/Widgets/Things/Utils/LastUpdateTs';
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { FloatingActionButton } from '@Primitive/FloatingActionButton';
import { LabelAutoSize } from '@Primitive/Typography';
import "./Canvas.less";
import { Props } from '@Utils/ReactProps';
// =======================================================================================================
export declare type CanvasItemLayoutDescDictionary = { [key: string]: CanvasItemLayoutDesc };
export declare type CanvasItemDescDictionary = { [key: string]: CanvasItemDesc };
export interface CanvasLayout {
    LayoutDesc: CanvasItemLayoutDescDictionary;
    ItemDesc: CanvasItemDescDictionary;
}
// ------------------------------------------------------------------------------------------------------
export enum EditMode {
    None,
    Resize,
    Move
}
// ------------------------------------------------------------------------------------------------------
export enum CanvasButtonEvent {
    Settings,
    Delete,
    Hide,
    Copy
}
// ------------------------------------------------------------------------------------------------------
export interface CanvasState {
    // The running layout
    Layout: CanvasLayout;

    // The conversion of W/H to pixels
    Pixels: { W: number, H: number };
    InvAspectRatio: number;

    // Canvas Padding
    Padding: number;

    // Edit states
    EditItemKey: string;
    EditMode: EditMode;

    LayoutDescInEditMode: { [Key: string]: CanvasItemLayoutDesc };

    // used only when `FillHorizontalGaps` is enabled
    LayoutDescOrderInEditMode: CanvasItemLayoutDesc[];
    LayoutDescStaticItemPositions: { [key: string]: number };

    // Move items
    MovePosition?: { X: number, Y: number }
    MoveInitialOffset?: { X: number, Y: number }

    // Resize
    ResizeSize?: { W: number, H: number }
    ResizeItemOrigin?: { X: number, Y: number }

    // Placeholder div
    PlaceholderDiv?: CanvasItemLayoutDesc;
}
// ------------------------------------------------------------------------------------------------------
export interface ElementSettings {
    // Widget no background
    NoBackground?: boolean;

    // Background color
    BackgroundColor?: string;

    // The zDepth of the paper shadow.
    zDepth: number;

    // Define round corners
    BorderRadius?: number;

    // Line that is drawn around the widget. 
    Outline?: IOutline;

    // Overflow settings
    Overflow?: Property.Overflow;
}
// ------------------------------------------------------------------------------------------------------
export interface CanvasProps extends Props<Canvas>, IComponentSizeProp {
    // The layout of components
    Layout: CanvasLayout;

    // The size of the discrete grid
    SizeW: number;

    // The width that we start drawing in mobile mode / list
    WidthListThreshold: number;

    // Margin between of items
    Margin: { W: number, H: number };

    // In this mode we can modify the items
    InEditMode: boolean;

    // In this case we allow items to be placed from top left to right and down, filling gaps in the horizontal axis
    // Can be used to change order of items in a list
    FillHorizontalGaps?: boolean;

    // Auto arrange the items in high to avoid any empty space if is possible
    CompactLayout: boolean;

    // Be able to have image as a background
    BackgroundImage?: string;
    MobileBackgroundImage?: string;

    // Enable the rounding of positions
    GridLayout?: boolean;

    // Default settings of elements
    ElementSettings: ElementSettings;

    CanvasBackgroundColor: string;

    // List with any selected item
    SelectedItems?: string[];

    // Static pixels for width/height
    HeightPixels?: number;
    WidthPixels?: number;

    // Events -------
    onCanvasLayoutChanged?: (newLayout: CanvasLayout) => void; // TBD
    onButtonTap?: (key: string, eventType: CanvasButtonEvent) => void;
    toolbarButtonsToHide?: CanvasButtonEvent[];

    // Aspect ratio
    AspectRatio?: number;

    // Style for the root HTML div element.
    rootStyle?: CSSProperties;

    // add extra button margin in canvas
    extraCanvasMargin?: number;

    // Add padding in canvas
    Padding?: number;

    // Disable scrolling. Default: false
    DisableScrollingY?: boolean;
}
// ------------------------------------------------------------------------------------------------------
export class Canvas extends Component<CanvasProps, CanvasState> {
    mainDiv: HTMLDivElement;
    itemDivs: { [key: string]: HTMLDivElement };
    // ---------------------------------------------------------------------------------------------------
    /**
     * Default Canvas aspect ratio inverse
     */
    public static readonly InvAspectRatio: number = 1 / (2 * 1.618033988749895);/* Phi!!! */

    /**
     * Default size width in units
     */
    private static readonly Size_W: number = 12;
    // ---------------------------------------------------------------------------------------------------
    public static GetWidthPixels(width: number, SizeW: number = Canvas.Size_W, padding: number): number {
        return (width - 2 * padding) / SizeW;
    }
    // ---------------------------------------------------------------------------------------------------
    public static GetPixels(opts: {
        width: number,
        sizeW?: number,
        heightPixels?: number,
        widthPixels?: number,
        invAspectRatio?: number, padding?: number
    }): { W: number, H: number } {
        const widthPixels = isDefined(opts.widthPixels)
            ? opts.widthPixels
            : Canvas.GetWidthPixels(opts.width, opts.sizeW, opts.padding);

        const heightPixels = isDefined(opts.heightPixels)
            ? opts.heightPixels
            : widthPixels * (isDefined(opts.invAspectRatio) ? opts.invAspectRatio : Canvas.InvAspectRatio);

        return { W: widthPixels, H: heightPixels };
    }
    // ---------------------------------------------------------------------------------------------------
    constructor(props: CanvasProps) {
        super(props);
        const { Padding, AspectRatio, Layout, width, SizeW, HeightPixels, WidthPixels } = this.props;
        const padding = (isDefined(Padding)) ? Padding : 5;
        const invAspRatio = Canvas.GetInvAspectRatio(AspectRatio);
        this.state = {
            Layout: _.cloneDeep(Layout),

            InvAspectRatio: invAspRatio,
            Pixels: Canvas.GetPixels({
                width: width,
                sizeW: SizeW,
                heightPixels: HeightPixels,
                widthPixels: WidthPixels,
                invAspectRatio: invAspRatio,
                padding: padding
            }),

            Padding: padding,

            EditItemKey: "",
            LayoutDescInEditMode: {},
            LayoutDescOrderInEditMode: null,
            LayoutDescStaticItemPositions: null,
            EditMode: EditMode.None,
            MovePosition: undefined,
            MoveInitialOffset: undefined,

            PlaceholderDiv: undefined
        };

        // Empty items divs
        this.itemDivs = {};
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidMount() {
        // Correct layout
        this.setState((prevState) => {
            Canvas._layoutDefaultCorrection(prevState.Layout, this.props);
            Canvas._layoutAutoCompact(prevState, this.props);
            return prevState;
        });
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: CanvasProps, prevState: CanvasState) {
        // Update the translation of pixels because we have different canvas size
        if (!_.isEqual(this.props.Layout, prevProps.Layout))
            this.setState((state: CanvasState) => {
                // If we have new layout reset the moving staffs

                // Override old layout
                state.Layout = _.cloneDeep(this.props.Layout);

                state.EditItemKey = "";
                state.LayoutDescInEditMode = {};
                state.LayoutDescOrderInEditMode = null;
                state.LayoutDescStaticItemPositions = null;
                state.EditMode = EditMode.None;
                state.MovePosition = undefined;
                state.MoveInitialOffset = undefined;
                state.PlaceholderDiv = undefined;
                // Empty items divs
                this.itemDivs = {};

                // Correct layout
                Canvas._layoutDefaultCorrection(state.Layout, this.props);
                Canvas._layoutAutoCompact(state, this.props);
                return state;
            });
    }
    // ------------------------------------------------------------------------------------------------------
    static getDerivedStateFromProps(nextProps: CanvasProps, prevState: CanvasState) {
        const padding = (isDefined(nextProps.Padding)) ? nextProps.Padding : 5;
        let newState: Partial<CanvasState> = {};
        newState.Padding = padding;
        newState.InvAspectRatio = Canvas.GetInvAspectRatio(nextProps.AspectRatio);
        newState.Pixels = Canvas.GetPixels({
            width: nextProps.width,
            sizeW: nextProps.SizeW,
            heightPixels: nextProps.HeightPixels,
            widthPixels: nextProps.WidthPixels,
            invAspectRatio: prevState.InvAspectRatio,
            padding: padding
        });
        return newState;
    }
    // ---------------------------------------------------------------------------------------------------
    // Aspect ratio inverse
    // ---------------------------------------------------------------------------------------------------
    public static GetInvAspectRatio(ratio: number) {
        return isDefined(ratio) && ratio !== 0 ? 1 / ratio : Canvas.InvAspectRatio;
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Auto correct layout (this function mutates the object passed in)
     * @param layout
     * @param props
     */
    private static _layoutDefaultCorrection(layout: CanvasLayout, props: CanvasProps) {
        _.forOwn(layout.LayoutDesc, (l, lKey) => {
            // The default state of the items is draggable and resizable
            if (!isDefined(l.IsStatic))
                l.IsStatic = false;
            if (!isDefined(l.IsDraggable))
                l.IsDraggable = true;
            if (!isDefined(l.IsResizable))
                l.IsResizable = true;

            // If is static then is not draggable or resizable
            if (l.IsStatic) {
                l.IsDraggable = false;
                l.IsResizable = false;
            }
        });

        this._fillHorizontalGapsInLayout(layout, props);
    }
    // ---------------------------------------------------------------------------------------------------
    // Auto re-order
    // ---------------------------------------------------------------------------------------------------
    public static LayoutCollisionCheck(l1: CanvasItemLayoutDesc, l2List: CanvasItemLayoutDesc[]): boolean {
        return Canvas._layoutCollisionTestArray(l1, l2List);
    }
    // ---------------------------------------------------------------------------------------------------
    // Given one layoutitems list and a specific layoutitem, check if they collide.
    private static _layoutCollisionTestArray(l1: CanvasItemLayoutDesc, l2List: CanvasItemLayoutDesc[]): boolean {
        for (var i = 0; i < l2List.length; i++) {
            let l2 = l2List[i];
            if (l1.Key === l2.Key) continue; // same element
            if (l1.X + l1.W <= l2.X) continue; // l1 is left of l2
            if (l1.X >= l2.X + l2.W) continue; // l1 is right of l2
            if (l1.Y + l1.H <= l2.Y) continue; // l1 is above l2
            if (l1.Y >= l2.Y + l2.H) continue; // l1 is below l2
            return true; // boxes overlap
        }
        return false; // no collision
    }
    // ---------------------------------------------------------------------------------------------------
    public static CompactLayoutDesc(LayoutDesc: CanvasItemLayoutDescDictionary) {
        Canvas._actualLayoutCompact(LayoutDesc);
    }
    // ---------------------------------------------------------------------------------------------------
    /**
     * Auto compact layout (this function mutates the object passed in)
     * @param state
     * @param moveLayout
     */
    private static _layoutAutoCompact(state: CanvasState, props: CanvasProps, moveLayout?: CanvasItemLayoutDesc) {
        // if the compact layout is disabled or horizontal gap fill is enabled; return
        if (!props.CompactLayout || props.FillHorizontalGaps)
            return;

        // Move the elements in array
        if (state.EditItemKey != "") {
            Canvas._actualLayoutCompact(state.LayoutDescInEditMode, moveLayout);
        } else {
            Canvas._actualLayoutCompact(state.Layout.LayoutDesc, moveLayout);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private static _actualLayoutCompact(layoutArray: CanvasItemLayoutDescDictionary, moveLayout?: CanvasItemLayoutDesc) {
        // Start to move elements to make the layout more compact
        let somethingMoved = true;
        while (somethingMoved) {
            somethingMoved = false;

            _.forOwn(layoutArray, l1 => {
                if (l1.DefiesGrid) return; // skip elements that defy grid
                if (isDefined(moveLayout) && l1.Key == moveLayout.Key) return; // avoid to move the moved layout
                if (l1.Y == 0) return; // the first in stack

                let minY = l1.Y;

                _.forOwn(layoutArray, l2 => {
                    if (l1 === l2) return; // same element
                    if (l1.X + l1.W <= l2.X) return; // l1 is left of l2
                    if (l1.X >= l2.X + l2.W) return; // l1 is right of l2
                    if (l1.Y + l1.H <= l2.Y) return; // l1 is above l2

                    if (l1.Y >= l2.Y + l2.H) {  // l1 is below l2
                        let diff = l1.Y - (l2.Y + l2.H);
                        if (minY > diff)
                            minY = diff;
                    }
                });

                // Check for move layout
                if (isDefined(moveLayout) &&
                    !((l1.X + l1.W <= moveLayout.X) ||       // l1 is left of l2
                        (l1.X >= moveLayout.X + moveLayout.W) || // l1 is right of l2
                        (l1.Y + l1.H <= moveLayout.Y)) &&        // l1 is above l2
                    (l1.Y >= moveLayout.Y + moveLayout.H)) { // l1 is below l2
                    let diff = l1.Y - (moveLayout.Y + moveLayout.H);
                    if (minY > diff)
                        minY = diff;
                }

                if (minY > 0) {
                    somethingMoved = true;
                    l1.Y = l1.Y - minY;
                }
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    /**
     * Get layout items sorted from top left to right and down.
     * @param layoutArray 
     */
    public static SortLayoutArray(layoutArray: CanvasItemLayoutDesc[]): CanvasItemLayoutDesc[] {
        return layoutArray.sort((a, b) => {
            if (a.Y > b.Y || (a.Y === b.Y && a.X > b.X)) {
                return 1;
            }
            return -1;
        })
    }
    // ---------------------------------------------------------------------------------------------------
    public static LayoutAutoReorder(layoutArray: CanvasItemLayoutDesc[],
        moveLayout: CanvasItemLayoutDesc = null,
        compactLayout: boolean = true
    ) {

        let layoutDescInEditMode: { [Key: string]: CanvasItemLayoutDesc } = {}

        // Get layout items sorted from top left to right and down.
        layoutArray = Canvas.SortLayoutArray(layoutArray);

        // find the first collision
        let collisionList = [moveLayout];
        let collisionId = layoutArray.findIndex(l => l != null && this._layoutCollisionTestArray(l, collisionList));
        while (collisionId >= 0) {
            let collision = layoutArray[collisionId];

            // remove the item from layout Array
            layoutArray[collisionId] = null;

            // find the new Y of collision
            let newY = collision.Y;
            let XStart = collision.X;
            let XEnd = collision.X + collision.W;
            for (var i = 0; i < collisionList.length; i++) {
                let c = collisionList[i];
                // The new item must interact with sub-collision item
                if (!((XEnd <= c.X) ||       // l1 is left of l2
                    (XStart >= c.X + c.W))) { // l1 is right of l2
                    let candY = c.Y + c.H;
                    if (newY < candY)
                        newY = candY; // find the higher Y of the correct X
                }
            }
            collision.Y = newY;

            // save the new Collision to list and new layout
            collisionList.push(collision);
            layoutDescInEditMode[collision.Key] = collision;

            // find the next collision
            collisionId = layoutArray.findIndex(l => l != null && this._layoutCollisionTestArray(l, collisionList));
        }

        // Add to LayoutDescInEditMode the items that remain stable
        layoutArray.forEach(l => { if (l != null) layoutDescInEditMode[l.Key] = l });

        // Make more compact the layout
        if (compactLayout)
            Canvas._actualLayoutCompact(layoutDescInEditMode, moveLayout);

        return layoutDescInEditMode;
    }
    // ---------------------------------------------------------------------------------------------------
    /**
     * Automatic item re-ordering (this function mutates the object passed in)
     * @param state
     * @param moveLayout
     */
    private _layoutAutoReorder(state: CanvasState) {
        if (state.EditItemKey == "")
            return;

        const moveLayout = state.PlaceholderDiv;

        if (this.props.FillHorizontalGaps) {

            const handleStaticItems = !_.isEmpty(state.LayoutDescStaticItemPositions);
            const tempLayoutOrder = handleStaticItems ? _.cloneDeep(state.LayoutDescOrderInEditMode) : state.LayoutDescOrderInEditMode;
            // if horizontal gaps with static items && no grid layout are enabled; gridify values
            if (handleStaticItems && !this.props.GridLayout) {
                moveLayout.X = Math.round(moveLayout.X);
                moveLayout.Y = Math.round(moveLayout.Y);
            }

            let layoutValid = true;

            // try find index in which we have a collision
            let collisionList = [moveLayout];

            let collisionId = state.LayoutDescOrderInEditMode.findIndex(l => Canvas._layoutCollisionTestArray(l, collisionList));
            if (collisionId !== -1) {
                // check if we have a collision with a static item (if needed)
                if (handleStaticItems && tempLayoutOrder[collisionId].IsStatic === true) {
                    layoutValid = false;
                }
                else {
                    // insert moveLayout at specific index
                    tempLayoutOrder.splice(collisionId, 0, _.cloneDeep(moveLayout));

                    // remove old entry
                    let oldIndex = -1;
                    for (let i = 0; i < tempLayoutOrder.length; i++) {
                        const el = tempLayoutOrder[i];
                        if (el.Key != moveLayout.Key || i == collisionId)
                            continue;

                        oldIndex = i;
                        tempLayoutOrder.splice(oldIndex, 1);
                        break;
                    }
                    if (oldIndex == -1) {
                        console.error('[YODIWO][CANVAS] Something is wrong with the horizontal gap filling algorithm !!!');
                        return;
                    }
                }
            }
            else {
                // check if element is placed at bottom of canvas
                let maxY = 0;
                let maxX = 0;
                _.forEach(tempLayoutOrder, l => {
                    if (l.Key == moveLayout.Key) return;
                    if (l.Y + l.H < maxY) return;
                    maxY = l.Y;
                    maxX = l.X;
                });

                if (moveLayout.Y > maxY || (moveLayout.Y == maxY && moveLayout.X > maxX)) {
                    // add element at end of array
                    const oldIndex = _.findIndex(tempLayoutOrder, l => l.Key == moveLayout.Key);
                    tempLayoutOrder.splice(oldIndex, 1)
                    tempLayoutOrder.push(moveLayout);
                }
            }

            // if we have static items
            // validate layout
            if (handleStaticItems) {
                for (let i = 0; i < tempLayoutOrder.length; i++) {
                    const el = tempLayoutOrder[i];

                    // try get cachedIndex
                    const cacheIndex = state.LayoutDescStaticItemPositions[el.Key];
                    if (!isDefined(cacheIndex)) continue;

                    // check for mismatch
                    if (cacheIndex != i) {
                        layoutValid = false;
                        break;
                    }
                }
            }

            // update layout; is it valid?
            if (layoutValid) {

                // update layout
                state.LayoutDescOrderInEditMode = tempLayoutOrder;

                // fill horizontal gaps based on update current LayoutDescOrderInEditMode
                state.LayoutDescInEditMode = Canvas._fillHorizontalGapsInLayoutArray(state.LayoutDescOrderInEditMode, this.props.SizeW, moveLayout);
            }
            else {
                // restore position to moved item
                const originalLayoutDesc = state.Layout.LayoutDesc[moveLayout.Key];
                moveLayout.X = originalLayoutDesc.X;
                moveLayout.Y = originalLayoutDesc.Y;
                return;
            }
        }
        else {

            let layoutArray: CanvasItemLayoutDesc[] = [];
            // todo: find a more efficient way to fill `layoutArray`
            _.forOwn(state.Layout.LayoutDesc, (value, lKey) => {
                if (state.EditItemKey != lKey) {
                    layoutArray.push(_.cloneDeep(value));
                }
            });
            state.LayoutDescInEditMode = Canvas.LayoutAutoReorder(layoutArray, moveLayout, this.props.CompactLayout);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    /**
     * Fill horizontal gaps in layout (this function mutates the object passed in)
     * @param layout
     * @param props
     */
    private static _fillHorizontalGapsInLayout(layout: CanvasLayout, props: CanvasProps) {
        // if horizontal gap fill is disabled; return
        if (!props.FillHorizontalGaps)
            return;

        // Get layout items sorted from top left to right and down.
        let layoutArray = Canvas.SortLayoutArray(_.values(layout.LayoutDesc));

        // from sorted layout, regenerate layout
        layout.LayoutDesc = this._fillHorizontalGapsInLayoutArray(layoutArray, props.SizeW);
    }
    // ---------------------------------------------------------------------------------------------------
    private static _fillHorizontalGapsInLayoutArray(
        layoutArray: CanvasItemLayoutDesc[],
        sizeW: number,
        moveLayout?: CanvasItemLayoutDesc): CanvasItemLayoutDescDictionary {

        // initialize layout
        const layout: CanvasItemLayoutDescDictionary = {};

        // reassign Xs,Ys; keep last X,Y
        let lastX = 0, lastY = 0;

        const updateLayoutFnc = !isDefined(moveLayout) ?
            (l: CanvasItemLayoutDesc) => {
                layout[l.Key] = l;
            } :
            (l: CanvasItemLayoutDesc) => {
                if (l.Key != moveLayout.Key)
                    layout[l.Key] = l;
            };

        _.forEach(layoutArray, (l) => {

            if (lastX + l.W > sizeW) {
                // update last X,Y; increase row
                lastY++;
                lastX = 0;
            }

            l.X = lastX;
            // update last X
            lastX += l.W;
            l.Y = lastY;

            // update layout
            updateLayoutFnc(l);
        });

        return layout;
    }
    // ---------------------------------------------------------------------------------------------------
    private _layoutAutoReorderSave(state: CanvasState) {
        let copyLayout = _.cloneDeep(state.Layout.LayoutDesc);
        _.forOwn(state.LayoutDescInEditMode, (value, key) => {
            if (_.has(copyLayout, key)) {
                copyLayout[key] = value;
            }
        });
        state.Layout.LayoutDesc = copyLayout;
        state.LayoutDescInEditMode = {};
        state.LayoutDescOrderInEditMode = null;
        state.LayoutDescStaticItemPositions = null;
    }
    // ---------------------------------------------------------------------------------------------------
    private _sendOnCanvasLayoutChanged() {
        const { onCanvasLayoutChanged } = this.props;
        if (!isDefined(onCanvasLayoutChanged))
            return;

        // If we have changes send event
        const { Layout } = this.state;
        if (!_.isEqual(this.props.Layout.LayoutDesc, Layout.LayoutDesc)) {
            onCanvasLayoutChanged(_.cloneDeep(Layout));
        }
    }
    // ---------------------------------------------------------------------------------------------------
    // Resize mechanism
    // ---------------------------------------------------------------------------------------------------
    private _resizeAnimation(EditItemKey: string) {
        const { Layout, Pixels } = this.state;
        if (EditItemKey != "") {
            const { LayoutDescInEditMode, EditItemKey, PlaceholderDiv } = this.state;
            // Move all elements by using direct css access
            _.forOwn(LayoutDescInEditMode, (l, lKey) => {
                let itemDiv = this.itemDivs[lKey];
                if (itemDiv != null) {
                    let x = Math.round(l.X * Pixels.W);
                    let y = Math.round(l.Y * Pixels.H);
                    let translate = "translate(" + x + "px," + y + "px)";
                    if (itemDiv.style.transform != translate) {
                        itemDiv.style.transform = translate;
                        itemDiv.style.webkitTransform = translate;
                        itemDiv.style.zIndex = '10';
                    }
                }
            });

            // Hide
            let itemDiv = this.itemDivs[EditItemKey];
            if (itemDiv != null) {
                itemDiv.style.display = 'none';
            }

            // resize the placeholder
            if (PlaceholderDiv != null) {
                let placeholderDiv = this.itemDivs["placehold-div"];
                if (placeholderDiv != null) {
                    let x = Math.round(PlaceholderDiv.X * Pixels.W);
                    let y = Math.round(PlaceholderDiv.Y * Pixels.H);
                    let translate = "translate(" + x + "px," + y + "px)";
                    placeholderDiv.style.transform = translate;
                    placeholderDiv.style.webkitTransform = translate;
                    placeholderDiv.style.width = (PlaceholderDiv.W * Pixels.W) + "px";
                    placeholderDiv.style.height = (PlaceholderDiv.H * Pixels.H) + "px";
                    placeholderDiv.style.display = 'block';
                }
            }

            // Request the next animation frame
            window.requestAnimationFrame(() => { this._resizeAnimation(EditItemKey) });
        } else {

            // Move all elements by using direct css access
            _.forOwn(Layout.LayoutDesc, (l, lKey) => {
                let itemDiv = this.itemDivs[lKey];
                if (itemDiv != null) {
                    let x = Math.round(l.X * Pixels.W);
                    let y = Math.round(l.Y * Pixels.H);
                    let translate = "translate(" + x + "px," + y + "px)";
                    itemDiv.style.transform = translate;
                    itemDiv.style.webkitTransform = translate;
                    itemDiv.style.zIndex = '10';
                    itemDiv.style.display = 'block';
                }
            });

            // Hide the placeholder
            let placeholdDiv = this.itemDivs["placehold-div"];
            if (placeholdDiv != null) {
                placeholdDiv.style.display = 'none';
            }
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _ResizeStart = (key: string, event: MouseEvent<any>) => {
        let item = this.itemDivs[key];
        if (item == null)
            return;
        let rect = item.getBoundingClientRect();
        const ResizeItemOrigin = {
            X: rect.left + window.pageXOffset,
            Y: rect.top + window.pageYOffset
        }
        this.setState((s: CanvasState) => {
            s.EditItemKey = key;
            s.ResizeItemOrigin = ResizeItemOrigin;
            s.EditMode = EditMode.Resize;
            s.PlaceholderDiv = _.cloneDeep(s.Layout.LayoutDesc[s.EditItemKey]);

            if (this.props.FillHorizontalGaps) {
                // prepare order array
                s.LayoutDescOrderInEditMode = Canvas.SortLayoutArray(_.cloneDeep(_.values(s.Layout.LayoutDesc)));

                // prepare static items cache
                s.LayoutDescStaticItemPositions = {};
                _.forEach(s.LayoutDescOrderInEditMode, (l, i) => {
                    if (l.IsStatic)
                        s.LayoutDescStaticItemPositions[l.Key] = i;
                });
            }

            return s;
        }, () => {
            // Request the next animation frame
            window.requestAnimationFrame(() => { this._resizeAnimation(key) });
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private _Resize(dist: { H: number, W: number }) {
        this.setState((s: CanvasState) => {
            // find the quantized positions for placeholder
            const oldW = s.PlaceholderDiv.W;
            const oldH = s.PlaceholderDiv.H;

            s.PlaceholderDiv.W = dist.W / s.Pixels.W;
            s.PlaceholderDiv.H = dist.H / s.Pixels.H;

            const { GridLayout, SizeW } = this.props;
            if (GridLayout && !s.PlaceholderDiv.DefiesGrid) {
                s.PlaceholderDiv.W = Math.round(s.PlaceholderDiv.W);
                s.PlaceholderDiv.H = Math.round(s.PlaceholderDiv.H);
            } else {
                s.PlaceholderDiv.W = Math.round(s.PlaceholderDiv.W * 20) / 20;
                s.PlaceholderDiv.H = Math.round(s.PlaceholderDiv.H * 20) / 20;
            }

            if (s.PlaceholderDiv.W <= 0) s.PlaceholderDiv.W = 1;
            if (s.PlaceholderDiv.W + s.PlaceholderDiv.X > SizeW) s.PlaceholderDiv.W = SizeW - s.PlaceholderDiv.X;
            if (s.PlaceholderDiv.H <= 0) s.PlaceholderDiv.H = 1;

            // Limit based on selected item
            let itemDesc = s.Layout.LayoutDesc[s.EditItemKey];
            if (itemDesc != null) {
                if (s.PlaceholderDiv.H < itemDesc.MinH) s.PlaceholderDiv.H = itemDesc.MinH;
                if (s.PlaceholderDiv.W < itemDesc.MinW) s.PlaceholderDiv.W = itemDesc.MinW;
                if (s.PlaceholderDiv.H > itemDesc.MaxH) s.PlaceholderDiv.H = itemDesc.MaxH;
                if (s.PlaceholderDiv.W > itemDesc.MaxW) s.PlaceholderDiv.W = itemDesc.MaxW;
            }

            // Reorder layout only when we have change in placehold
            if (s.PlaceholderDiv.W != oldW ||
                s.PlaceholderDiv.H != oldH) {
                this._layoutAutoReorder(s);
            }

            s.ResizeSize = dist;
            return s;
        });
    }
    // ---------------------------------------------------------------------------------------------------
    // Move mechanism
    // ---------------------------------------------------------------------------------------------------
    private _Move(position: { X: number, Y: number }) {
        this.setState((s: CanvasState) => {
            // find the quantized positions for placeholder
            const { PlaceholderDiv } = s;
            const oldX = PlaceholderDiv.X;
            const oldY = PlaceholderDiv.Y;
            PlaceholderDiv.X = position.X / s.Pixels.W;
            PlaceholderDiv.Y = position.Y / s.Pixels.H;

            const { GridLayout, SizeW } = this.props;
            if (GridLayout && !PlaceholderDiv.DefiesGrid) {
                PlaceholderDiv.X = Math.floor(PlaceholderDiv.X);
                PlaceholderDiv.Y = Math.floor(PlaceholderDiv.Y);
            } else {
                PlaceholderDiv.X = Math.floor(PlaceholderDiv.X * 20) / 20;
                PlaceholderDiv.Y = Math.floor(PlaceholderDiv.Y * 20) / 20;
            }

            // Check the limits
            if (PlaceholderDiv.X < 0) PlaceholderDiv.X = 0;
            if (PlaceholderDiv.X >= SizeW) PlaceholderDiv.X = SizeW - 1;
            if (PlaceholderDiv.X + PlaceholderDiv.W >= SizeW) PlaceholderDiv.X = SizeW - PlaceholderDiv.W;
            if (PlaceholderDiv.Y < 0) PlaceholderDiv.Y = 0;

            // Reorder layout only when we have change in placehold
            if (PlaceholderDiv.X != oldX ||
                PlaceholderDiv.Y != oldY) {
                this._layoutAutoReorder(s);
            }
            s.MovePosition = position;
            return s;
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private _moveAnimation(_editItemKey: string) {
        const { LayoutDescInEditMode, Layout,
            Pixels, MovePosition, EditItemKey,
            PlaceholderDiv } = this.state;
        if (_editItemKey != "") {
            // Move all elements by using direct css access
            _.forOwn(LayoutDescInEditMode, (l, lKey) => {
                let itemDiv = this.itemDivs[lKey];
                if (itemDiv != null) {
                    let x = Math.round(l.X * Pixels.W);
                    let y = Math.round(l.Y * Pixels.H);
                    let translate = "translate(" + Math.round(x) + "px," + Math.round(y) + "px)";
                    if (itemDiv.style.transform != translate) {
                        itemDiv.style.transform = translate;
                        itemDiv.style.webkitTransform = translate;
                        itemDiv.style.zIndex = '10';
                    }
                }
            });

            // Move the basic item
            let itemDiv = this.itemDivs[EditItemKey];
            if (itemDiv != null &&
                MovePosition != null) {
                let translate = "translate(" + MovePosition.X + "px," + MovePosition.Y + "px)";
                if (itemDiv.style.transform != translate) {
                    itemDiv.style.transform = translate;
                    itemDiv.style.webkitTransform = translate;
                    itemDiv.style.zIndex = '20';
                }
            }

            // Move and the placeholder
            let placeholdDiv = this.itemDivs["placehold-div"];
            if (placeholdDiv != null &&
                PlaceholderDiv != null) {
                let x = Math.round(PlaceholderDiv.X * Pixels.W);
                let y = Math.round(PlaceholderDiv.Y * Pixels.H);
                let translate = "translate(" + x + "px," + y + "px)";
                if (placeholdDiv.style.transform != translate) {
                    placeholdDiv.style.transform = translate;
                    placeholdDiv.style.webkitTransform = translate;
                    placeholdDiv.style.display = 'block';
                    placeholdDiv.style.width = (PlaceholderDiv.W * Pixels.W) + "px";
                    placeholdDiv.style.height = (PlaceholderDiv.H * Pixels.H) + "px";
                }
            }

            // Request the next animation frame
            window.requestAnimationFrame(() => { this._moveAnimation(EditItemKey) });
        } else {
            // Move all elements by using direct css access
            _.forOwn(Layout.LayoutDesc, (l, lKey) => {
                let itemDiv = this.itemDivs[lKey];
                if (itemDiv != null) {
                    let x = Math.round(l.X * Pixels.W);
                    let y = Math.round(l.Y * Pixels.H);
                    let translate = "translate(" + x + "px," + y + "px)";
                    itemDiv.style.transform = translate;
                    itemDiv.style.webkitTransform = translate;
                    itemDiv.style.zIndex = '10';
                }
            });

            // Move and the placeholder
            let placeholdDiv = this.itemDivs["placehold-div"];
            if (placeholdDiv != null) {
                placeholdDiv.style.display = 'none';
            }
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _MoveMouseStart = (key: string, event: MouseEvent<any>) => {
        let moveDiv = this.itemDivs[key];
        if (moveDiv == null)
            return;
        let rect = moveDiv.getBoundingClientRect();
        const MoveInitialOffset = {
            X: event.pageX - rect.left - window.pageXOffset,
            Y: event.pageY - rect.top - window.pageYOffset
        }

        this.setState((s: CanvasState) => {
            s.EditItemKey = key;
            s.MoveInitialOffset = MoveInitialOffset
            s.EditMode = EditMode.Move;
            s.PlaceholderDiv = _.cloneDeep(s.Layout.LayoutDesc[s.EditItemKey]);

            if (this.props.FillHorizontalGaps) {
                // prepare order array
                s.LayoutDescOrderInEditMode = Canvas.SortLayoutArray(_.cloneDeep(_.values(s.Layout.LayoutDesc)));

                // prepare static items cache
                s.LayoutDescStaticItemPositions = {};
                _.forEach(s.LayoutDescOrderInEditMode, (l, i) => {
                    if (l.IsStatic)
                        s.LayoutDescStaticItemPositions[l.Key] = i;
                });
            }

            return s;
        }, () => {
            // Start the animation/rendering
            window.requestAnimationFrame(() => { this._moveAnimation(key) });
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private _MouseMove = (event: MouseEvent<any>) => {
        if (this.state.EditItemKey == "")
            return;

        if (this.state.EditMode == EditMode.Resize) {
            const { ResizeItemOrigin } = this.state;
            let dist = {
                W: event.pageX - ResizeItemOrigin.X,
                H: event.pageY - ResizeItemOrigin.Y
            }
            this._Resize(dist);
        } else {
            let clientRect: ClientRect = this.mainDiv.getBoundingClientRect();
            if (clientRect == null)
                return;
            const { MoveInitialOffset } = this.state;

            let position = {
                X: event.pageX - clientRect.left + this.mainDiv.scrollLeft - MoveInitialOffset.X,
                Y: event.pageY - clientRect.top + this.mainDiv.scrollTop - MoveInitialOffset.Y
            }
            this._Move(position);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _MouseStop = () => {
        this.setState((s: CanvasState) => {

            let editItemKey = s.EditItemKey;
            s.EditItemKey = "";

            // Set the new layout
            let layoutDesc = s.Layout.LayoutDesc[editItemKey];
            if (!_.isEqual(layoutDesc, s.PlaceholderDiv)) {
                layoutDesc.X = s.PlaceholderDiv.X;
                layoutDesc.Y = s.PlaceholderDiv.Y;
                layoutDesc.H = s.PlaceholderDiv.H;
                layoutDesc.W = s.PlaceholderDiv.W;
            }

            s.PlaceholderDiv = undefined;

            this._layoutAutoReorderSave(s);

            s.EditMode = EditMode.None;
            s.MovePosition = undefined;
            s.ResizeSize = undefined;

            Canvas._layoutAutoCompact(s, this.props);
            return s;
        }, () => {
            this._sendOnCanvasLayoutChanged();
        });
    }
    // ---------------------------------------------------------------------------------------------------
    // Rendering
    // ---------------------------------------------------------------------------------------------------
    public static GetDimensions(layoutDesc: CanvasItemLayoutDesc, Pixels: { W: number, H: number }): { x: number, y: number, w: number, h: number } {
        return {
            x: Math.round(layoutDesc.X * Pixels.W),
            y: Math.round(layoutDesc.Y * Pixels.H),
            w: Math.round(layoutDesc.W * Pixels.W),
            h: Math.round(layoutDesc.H * Pixels.H)
        }
    }
    // ---------------------------------------------------------------------------------------------------
    renderItem(layoutDesc: CanvasItemLayoutDesc,
        itemDesc: CanvasItemDesc,
        doNotRedraw: boolean): ReactNode {

        const {
            SelectedItems, ElementSettings,
            Margin, extraCanvasMargin,
            InEditMode,
            onButtonTap, toolbarButtonsToHide
        } = this.props;

        const { Layout, Pixels, EditItemKey, ResizeSize } = this.state;
        const { x, y, w, h } = Canvas.GetDimensions(layoutDesc, Pixels);

        let displayStyle = 'block';

        if (itemDesc.Key == EditItemKey &&
            ResizeSize != undefined) {
            displayStyle = 'none';
        }

        let translate = "translate(" + x + "px," + y + "px)";

        // Move all elements by using direct css access
        _.forOwn(Layout.LayoutDesc, (l, lKey) => {
            let isSelected = isDefined(SelectedItems) && SelectedItems.length > 0 && (SelectedItems.findIndex((iKey) => iKey == lKey) !== -1);
            let itemDiv = this.itemDivs[lKey];
            if (itemDiv != null) {
                itemDiv.style.zIndex = (isSelected) ? '100' : '10';
            }
        });

        let isSelected = isDefined(SelectedItems) && SelectedItems.length > 0 && (SelectedItems.findIndex((iKey) => iKey == itemDesc.Key) !== -1);
        let elementSettings = _.merge({}, ElementSettings, {
            NoBackground: layoutDesc.WidgetNoBackground,
            zDepth: itemDesc.zDepth
        } as Partial<ElementSettings>);
        return (
            <div
                key={"canvas-item-key" + itemDesc.Key}
                className="noselect"
                style={{
                    position: "absolute",
                    width: w - Margin.W,
                    height: h - Margin.H,
                    paddingRight: Margin.W,
                    paddingBottom: Margin.H,
                    zIndex: 3,
                    transform: translate,
                    WebkitTransform: translate,
                    display: displayStyle,
                    marginBottom: (isDefined(extraCanvasMargin)) ? extraCanvasMargin : 0
                }}
                ref={(div) => { this.itemDivs[itemDesc.Key] = div }}>
                {
                    (InEditMode) ?
                        <CanvasEditItem
                            height={h - Margin.H}
                            width={w - Margin.W}
                            LayoutDesc={layoutDesc}
                            ItemDesc={itemDesc}
                            IsSelected={isSelected}
                            MouseMoveStart={this._MoveMouseStart}
                            MouseResizeStart={this._ResizeStart}
                            onButtonTap={onButtonTap}
                            toolbarButtonsToHide={toolbarButtonsToHide}
                            ElementSettings={elementSettings}
                            doNotRedraw={doNotRedraw}
                        />
                        :
                        <CanvasStaticItem
                            height={h - Margin.H}
                            width={w - Margin.W}
                            ItemDesc={itemDesc}
                            doNotRedraw={doNotRedraw}
                            ElementSettings={elementSettings}
                        />
                }
            </div >
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private renderItems(): ReactNode[] {
        const { LayoutDescInEditMode, EditItemKey, Layout } = this.state;
        let items: ReactNode[] = [];
        const doNotRedraw: boolean = _.keys(LayoutDescInEditMode).length > 0 && this.state.EditMode != EditMode.None;
        // When we are in moving or resizing mode we need to draw the modify layout
        if (EditItemKey == "" || _.keys(LayoutDescInEditMode).length == 0) {
            _.forOwn(Layout.LayoutDesc, (layoutDesc, key) => {
                if (_.has(Layout.ItemDesc, key)) {
                    let itemDesc = Layout.ItemDesc[key];
                    items.push(this.renderItem(layoutDesc, itemDesc, doNotRedraw));
                }
            });
        } else {
            _.forOwn(LayoutDescInEditMode, (layoutDesc, key) => {
                if (_.has(Layout.ItemDesc, key)) {
                    let itemDesc = Layout.ItemDesc[key];
                    items.push(this.renderItem(layoutDesc, itemDesc, doNotRedraw));
                }
            });
            // The LayoutDescInEditMode is not include the moving/resized one
            // we need to add the rendering by our self
            let layoutDesc = Layout.LayoutDesc[EditItemKey];
            let itemDesc = Layout.ItemDesc[EditItemKey];

            items.push(this.renderItem(layoutDesc, itemDesc, doNotRedraw));
        }
        return items;
    }
    // ---------------------------------------------------------------------------------------------------
    private renderMobileItems(width: number, pageHeight: number): ReactNode[] {
        //DebugEx.TraceVerbose("Show mobile mode canvas", width);

        const { Layout, LayoutDescInEditMode } = this.state;
        // sort item by mobile order number
        var sortedItems = _.sortBy(_.filter(Layout.LayoutDesc, (x, key) => _.has(Layout.ItemDesc, key)), x => x.MobileOrder);
        const { ElementSettings, SizeW, Margin } = this.props;

        if (sortedItems.length == 1) {
            let value = sortedItems[0];
            if (_.has(Layout.ItemDesc, value.Key)) {
                let itemDesc = Layout.ItemDesc[value.Key];
                let elementSettings = _.merge({}, ElementSettings, {
                    NoBackground: value.WidgetNoBackground,
                    zDepth: itemDesc.zDepth
                } as Partial<ElementSettings>);
                return [
                    <CanvasStaticItem
                        height={pageHeight}
                        width={width}
                        ItemDesc={itemDesc}
                        ElementSettings={elementSettings}
                        doNotRedraw={_.keys(LayoutDescInEditMode).length > 0}
                    />
                ];
            }
        } else {
            let items: ReactNode[] = [];

            const { Padding, InvAspectRatio } = this.state;
            // Increase the space for page scrolling
            width -= 2 * Padding;

            // TODO: Support edit in mobile ?!!!
            _.forEach(sortedItems, (value, index) => {
                if (_.has(Layout.ItemDesc, value.Key)) {
                    let itemDesc = Layout.ItemDesc[value.Key];
                    let height = (width * 4 / SizeW) * InvAspectRatio * value.H;
                    let elementSettings = _.merge({}, ElementSettings, {
                        NoBackground: value.WidgetNoBackground,
                        zDepth: itemDesc.zDepth
                    } as Partial<ElementSettings>);
                    items.push(
                        <div
                            key={"canvas-item-key" + itemDesc.Key}
                            className="noselect"
                            style={{
                                position: "relative",
                                width: width,
                                height: height,
                                marginBottom: Margin.H
                            }}
                            ref={(div) => { this.itemDivs[itemDesc.Key] = div }}>
                            <CanvasStaticItem
                                height={height}
                                width={width}
                                ItemDesc={itemDesc}
                                doNotRedraw={_.keys(LayoutDescInEditMode).length > 0}
                                ElementSettings={elementSettings}
                            />
                        </div >
                    );
                }
            });

            return items;
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const {
            width,
            height,
            WidthListThreshold,
            CanvasBackgroundColor,
            rootStyle,
            InEditMode,
            BackgroundImage,
            MobileBackgroundImage
        } = this.props;

        const { Padding } = this.state;
        let inMobileMode = width <= WidthListThreshold;

        return (
            <div
                key={"canvas-key"}
                style={{
                    width: width,
                    height: height,
                    padding: Padding,
                    overflowX: 'hidden',
                    overflowY: this.props.DisableScrollingY ? 'hidden' : 'auto',
                    backgroundColor: CanvasBackgroundColor,
                    ...rootStyle,
                    ...(inMobileMode && !isStringEmpty(MobileBackgroundImage) ? {
                        backgroundImage: `url(${MobileBackgroundImage})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover'
                    } : {})
                }}
                onMouseMove={InEditMode ? this._MouseMove : undefined}
                onMouseUp={InEditMode ? this._MouseStop : undefined}
                className="noselect"
                ref={(div) => { this.mainDiv = div }}>
                {inMobileMode
                    ? this.renderMobileItems(width - 2 * Padding, height - 2 * Padding)
                    : <div
                        className="noselect"
                        style={{
                            width: width - 2 * Padding,
                            height: (InEditMode ? 2 : 1) * height - 2 * Padding,
                            position: "relative"
                        }}>
                        {(!isStringEmpty(BackgroundImage)) ?
                            <img style={{ position: "absolute", width: width - 2 * Padding }} src={BackgroundImage} />
                            : null
                        }
                        <div
                            key={"canvas-render-placehold-key"}
                            className="noselect"
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: 0,
                                height: 0,
                                display: 'none',
                                backgroundColor: "rgba(255, 158, 1, 0.4)",
                                zIndex: 2,
                                transform: "translate(0px,0px)",
                                WebkitTransform: "translate(0px,0px)",
                            }}
                            ref={(div) => { this.itemDivs["placehold-div"] = div }}>
                        </div>
                        {this.renderItems()}
                    </div>
                }
            </div >
        );
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Internal canvas item staffs

interface CanvasEditItemProps extends IComponentSizeProp {
    IsSelected: boolean;
    ElementSettings: ElementSettings;

    // Descriptions
    ItemDesc: CanvasItemDesc;
    LayoutDesc: CanvasItemLayoutDesc;

    // Events
    MouseMoveStart: (key: string, event: MouseEvent<any>) => void;
    MouseResizeStart: (key: string, event: MouseEvent<any>) => void;

    onButtonTap: (key: string, eventType: CanvasButtonEvent) => void;
    toolbarButtonsToHide: CanvasButtonEvent[]
    /**
     * Used for disabling draw from upper level.
     * In Resize or Move EditMode we don't want to redraw the item
     */
    doNotRedraw: boolean;
}
// -------------------------------------------------------------------------------------------------------
interface CanvasEditItemState {
    showMore: boolean;
}
// -------------------------------------------------------------------------------------------------------
const toolbarButtons: { Key: CanvasButtonEvent, primaryText: string, icon: ReactElement<any> }[] = [
    { Key: CanvasButtonEvent.Settings, primaryText: "Settings", icon: <i className='fas fa-cog center' /> },
    { Key: CanvasButtonEvent.Delete, primaryText: "Delete", icon: <i className='fas fa-trash' /> },
    { Key: CanvasButtonEvent.Hide, primaryText: "Hide", icon: <i className='fas fa-eye-slash' /> },
    { Key: CanvasButtonEvent.Copy, primaryText: "Copy to clipboard", icon: <i className='fas fa-clone' /> }]
// ---------------------------------------------------------------------------------------------------
class CanvasEditItem extends Component<CanvasEditItemProps, CanvasEditItemState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: CanvasEditItemProps) {
        super(props);
        this.state = { showMore: false };
    }
    // ---------------------------------------------------------------------------------------------------
    shouldComponentUpdate(nextProps: CanvasEditItemProps, nextState: CanvasEditItemState): boolean {
        if (this.state.showMore != nextState.showMore)
            return true;
        return !nextProps.doNotRedraw && !_.isEqual(nextProps, this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const {
            onButtonTap, MouseMoveStart, MouseResizeStart,
            toolbarButtonsToHide,
            height, width, doNotRedraw,
            ItemDesc, LayoutDesc, IsSelected, ElementSettings
        } = this.props;
        const { showMore } = this.state;

        const visibleButtons = !isDefined(onButtonTap) ? null
            : isDefined(toolbarButtonsToHide)
                ? toolbarButtons.filter(value => {
                    return toolbarButtonsToHide.indexOf(value.Key) === -1;
                })
                : toolbarButtons;

        let baseCircleSize = 40;
        if (baseCircleSize > height || baseCircleSize > width) {
            // 4px for a slight space
            baseCircleSize = Math.min(height, width) - 4;
        }
        let secondaryCircleSize = Math.ceil(3 * baseCircleSize / 4);

        let resizeEdgeSize = 30;
        let resizeEdgeThickness = 5;

        if (resizeEdgeSize > height || resizeEdgeSize > width) {
            resizeEdgeSize = Math.min(height, width);
            resizeEdgeThickness = 3;
        }

        return <div>
            <CanvasStaticItem
                height={height}
                width={width}
                ItemDesc={ItemDesc}
                doNotRedraw={doNotRedraw}
                ElementSettings={ElementSettings} />
            <div
                key={"canvas-disable-overlay-key" + ItemDesc.Key}
                id={"disable-overlay"}
                className="noselect"
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: width,
                    height: height,
                    opacity: ItemDesc.IsHidden ? 0.9 : 0.6,
                    backgroundColor: ItemDesc.IsHidden ? '#542323' : '#2a3035',
                    zIndex: 1000
                }} />
            {LayoutDesc.IsResizable
                ? <div
                    key={"canvas-resize-handler-key" + ItemDesc.Key}
                    id={"resize-handler"}
                    className="noselect"
                    style={{
                        zIndex: 21000,
                        position: "absolute",
                        top: height - resizeEdgeSize,
                        left: width - resizeEdgeSize,
                        width: resizeEdgeSize,
                        height: resizeEdgeSize,
                        borderBottomColor: (IsSelected) ? '#1a8cff' : '#e68e00',
                        borderBottomStyle: 'solid',
                        borderBottomWidth: resizeEdgeThickness,
                        borderRightColor: (IsSelected) ? '#1a8cff' : '#e68e00',
                        borderRightStyle: 'solid',
                        borderRightWidth: resizeEdgeThickness,
                        cursor: 'nw-resize'
                    }}
                    onMouseDown={(t) => MouseResizeStart(ItemDesc.Key, t)} />
                : null}
            <div
                key={"canvas-toolbar-key" + ItemDesc.Key}
                id={"toolbar"}
                style={{
                    zIndex: 20000,
                    position: "absolute",
                    top: 2,
                    left: 2,
                    width: width
                }}>
                {LayoutDesc.IsDraggable
                    ? <FloatingActionButton
                        backgroundColor={IsSelected ? '#39f' : '#FF9E01'}
                        iconColor="white"
                        diameter={baseCircleSize}
                        onMouseDown={(t) => MouseMoveStart(ItemDesc.Key, t)}>
                        <i className='fas fa-arrows-alt center' style={{ fontSize: 24 }} />
                    </FloatingActionButton>
                    : null}
                {visibleButtons != null && visibleButtons.length > 0
                    ? <FloatingActionButton
                        backgroundColor={IsSelected ? '#39f' : '#FF9E01'}
                        iconColor="white"
                        diameter={secondaryCircleSize}
                        onClick={() => onButtonTap(ItemDesc.Key, visibleButtons[0].Key)}
                        style={{
                            position: "relative",
                            top: "-5px",
                            left: "2px"
                        }}>
                        {visibleButtons[0].icon}
                    </FloatingActionButton>
                    : null}
                {IsSelected
                    ? <FloatingActionButton
                        backgroundColor={IsSelected ? '#39f' : '#FF9E01'}
                        iconColor="white"
                        diameter={secondaryCircleSize}
                        onClick={() => this.setState({ showMore: !showMore })}
                        style={{
                            position: "relative",
                            top: "-5px",
                            left: "4px"
                        }}>
                        {showMore
                            ? <i className='fas fa-angle-up center' />
                            : <i className='fas fa-angle-down' />}
                    </FloatingActionButton>
                    : null}
                {IsSelected
                    ? <div
                        key={"canvas-toolbar-key" + ItemDesc.Key}
                        id={"toolbar"}
                        style={{
                            zIndex: 2000,
                            position: "absolute",
                            top: baseCircleSize,
                            left: baseCircleSize + 2,
                            width: secondaryCircleSize
                        }}>
                        {showMore
                            ? _.map(visibleButtons, (button, i) =>
                                (i > 0)
                                    ? <FloatingActionButton
                                        backgroundColor={IsSelected ? '#39f' : '#FF9E01'}
                                        iconColor="white"
                                        diameter={secondaryCircleSize}
                                        onClick={() => onButtonTap(ItemDesc.Key, button.Key)}>
                                        {button.icon}
                                    </FloatingActionButton>
                                    : null)
                            : null}
                    </div>
                    : null}
            </div>
        </div>;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Internal canvas item

interface CanvasStaticItemProps extends IComponentSizeProp {
    ElementSettings: ElementSettings;

    // Descriptions
    ItemDesc: CanvasItemDesc;

    /**
    * Used for disabling draw from upper level.
    * In Resize or Move EditMode we don't want to redraw the item
    */
    doNotRedraw: boolean;
}
// -------------------------------------------------------------------------------------------------------
interface CanvasStaticItemState {
    InZoomMode: boolean;
    hasTitleOverflow: boolean;
}
// -------------------------------------------------------------------------------------------------------
export class CanvasStaticItem extends Component<CanvasStaticItemProps, CanvasStaticItemState> {
    // ---------------------------------------------------------------------------------------------------
    private randomKey: string;
    // ---------------------------------------------------------------------------------------------------
    titleElement: HTMLDivElement;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: CanvasStaticItemProps) {
        super(props);
        this.randomKey = "paper-" + this.props.ItemDesc.Key + "-" + getRandomSmallKey();
        this.state = {
            InZoomMode: false,
            hasTitleOverflow: false
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.detectAndHandleTitleOverflow();
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate() {
        this.detectAndHandleTitleOverflow();
    }
    // ---------------------------------------------------------------------------------------------------
    detectAndHandleTitleOverflow = () => {
        if (this.titleElement != null) {
            // is there any text/horizontal overflow?
            if (this.titleElement.scrollWidth > this.titleElement.clientWidth) {
                if (!this.state.hasTitleOverflow) {
                    this.setState({ hasTitleOverflow: true });
                }
            } else {
                if (this.state.hasTitleOverflow) {
                    this.setState({ hasTitleOverflow: false });
                }
            }
        }
    }
    // ---------------------------------------------------------------------------------------------------
    shouldComponentUpdate(nextProps: CanvasStaticItemProps, nextState: CanvasStaticItemState): boolean {
        return !nextProps.doNotRedraw && (!_.isEqual(nextProps, this.props) || !_.isEqual(nextState, this.state));
    }
    // ------------------------------------------------------------------------------------------------------
    toggleZoom = () => this.setState({ InZoomMode: !this.state.InZoomMode });
    // ---------------------------------------------------------------------------------------------------
    getWidgetTitle = (width: number): { widgetTitle: any, widgetTitleHeight: number } => {
        const itemDesc = this.props.ItemDesc;
        let widgetTitle = null;
        let widgetTitleHeight = _.isNumber(itemDesc.TitleHeight) ? itemDesc.TitleHeight : 25;
        let widgetExtraToolbarMenu = itemDesc.RenderToolbar?.(itemDesc.Key, widgetTitleHeight);
        let dynamicTooltip = itemDesc.ToolbarDynamicTooltip?.();

        // Create widget's title.
        if (!_.isEmpty(itemDesc.Title)
            || !_.isEmpty(itemDesc.ToolbarIcon) || itemDesc.ShowExtraMenu
            || (_.isArray(widgetExtraToolbarMenu) && widgetExtraToolbarMenu.length > 0)) {

            const hasTitle = !IsNullOrWhiteSpace(itemDesc.Title);
            const toolbarTextWidth = width -
                // Left and right padding.
                20 -
                // Custom view-only icon.
                (!_.isEmpty(itemDesc.ToolbarIcon) ? 24 : 0) -
                // Zoom icon.
                (itemDesc.ShowExtraMenu ? 24 : 0) -
                // Extra nodes (except from the last update TS).
                _.filter(widgetExtraToolbarMenu,
                    v => (v as any).key != __LAST_UPDATE_TS_KEY__ ||
                        (v as any).props.viewFormat == ITsViewFormat.Tooltip).length * 24;

            const hasTooltip = !IsNullOrWhiteSpace(itemDesc.ToolbarTooltip) || dynamicTooltip != null
                && (_.isString(dynamicTooltip) && !IsNullOrWhiteSpace(dynamicTooltip) || _.isObject(dynamicTooltip));

            let titleComponent = null;
            if (hasTitle) {
                if (itemDesc.ToolbarFontAutoSize) {
                    titleComponent = (
                        <LabelAutoSize
                            Text={itemDesc.Title}
                            Height={widgetTitleHeight}
                            Width={toolbarTextWidth}
                            MaxFontSize={14}
                            Style={{
                                paddingLeft: 10,
                                paddingRight: 10
                            }} />
                    );
                } else {
                    titleComponent = (
                        <div
                            ref={ref => { this.titleElement = ref }}
                            style={{
                                width: toolbarTextWidth,
                                height: widgetTitleHeight,
                                lineHeight: widgetTitleHeight + "px",
                                fontSize: itemDesc.ToolbarFontSize,
                                paddingLeft: 10,
                                paddingRight: 10,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}>
                            {itemDesc.Title}
                        </div>
                    );

                    if (!hasTooltip && this.state.hasTitleOverflow) {
                        titleComponent = (
                            <Tippy
                                key='title-tooltip'
                                animation={false}
                                trigger='mouseenter'
                                placement='bottom'
                                content={itemDesc.Title}>
                                {titleComponent}
                            </Tippy>
                        );
                    }
                }
            }

            widgetTitle = <div key="widgetTitle" className="canvas-toolbar" style={{
                height: widgetTitleHeight,
                backgroundColor: itemDesc.ToolbarBackgroundColor,
                color: itemDesc.ToolbarFontColor,
                zIndex: 20000
            }}>
                <div
                    key="title"
                    style={{
                        fontWeight: 500,
                        color: itemDesc.ToolbarFontColor,
                        textAlign: itemDesc.Alignment
                    }}>
                    {!_.isEmpty(itemDesc.ToolbarIcon) ?
                        <div key="icon">
                            <i className={getIconClassName(itemDesc.ToolbarIcon)} />
                        </div>
                        : null}
                    {hasTooltip
                        ? <Tippy
                            key="tooltip"
                            animation={false}
                            trigger="mouseenter"
                            placement="bottom"
                            content={<div style={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 100,
                                minHeight: 20
                            }}>
                                {this.state.hasTitleOverflow && hasTitle ? <div>{itemDesc.Title}</div> : null}
                                <div>{itemDesc.ToolbarTooltip}</div>
                                <div>{dynamicTooltip}</div>
                            </div>}>
                            <div>
                                {titleComponent}
                            </div>
                        </Tippy>
                        : titleComponent}
                </div>
                <div key="icons">
                    {widgetExtraToolbarMenu}
                    {itemDesc.ShowExtraMenu ?
                        <i
                            key="zoom-icon"
                            style={{
                                fontWeight: "bold", fontSize: "14px",
                                lineHeight: widgetTitleHeight + "px",
                                color: "rgba(0, 0, 0, 0.87)",
                                paddingLeft: 10, cursor: "pointer",
                            }}
                            className={(this.state.InZoomMode) ? "fa fa-times" : "fa fa-external-link"}
                            onClick={this.toggleZoom} />
                        : null}
                </div>
            </div>
        } else
            widgetTitleHeight = 0;

        return { widgetTitle, widgetTitleHeight };
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const itemDesc = this.props.ItemDesc;

        // Create the widget container
        if (this.state.InZoomMode) {
            return <ExDialog
                Enable={true}
                Conf={{
                    contentStaticSize: { w: 0.98, h: 0.95 },
                    bodyFunc: (w, h) => {
                        let { widgetTitle, widgetTitleHeight } = this.getWidgetTitle(w);
                        return <div
                            id={"widget-" + this.props.ItemDesc.Key}
                            key={this.randomKey}
                            className="noselect"
                            style={{ width: w, height: h - widgetTitleHeight }}>
                            {widgetTitle}
                            <div key="body" style={{ width: w, height: h - widgetTitleHeight }}>
                                {itemDesc.Render(itemDesc.Key, w, h - widgetTitleHeight)}
                            </div>
                        </div>
                    },
                    handleClose: this.toggleZoom
                }}
            />
        } else {
            let width = this.props.width;
            let { widgetTitle, widgetTitleHeight } = this.getWidgetTitle(width);
            let height = this.props.height - widgetTitleHeight;
            const elSettings = this.props.ElementSettings;

            let style: React.CSSProperties = {
                boxShadow: getZDepthShadow(elSettings.zDepth),
                width: this.props.width,
                height: this.props.height,
                display: 'block',
                borderRadius: elSettings.BorderRadius,
                overflow: elSettings.Overflow ?? "hidden"
            };

            

            // Reset background and shadow if we have flag for no background
            if (elSettings.NoBackground) {
                style.backgroundColor = "transparent";
                style.boxShadow = "";
            } else {
                style.backgroundColor = (!isStringEmpty(itemDesc.BackgroundColor)) ? itemDesc.BackgroundColor :
                    ((!isStringEmpty(elSettings.BackgroundColor)) ? elSettings.BackgroundColor :
                        "rgb(255, 255, 255)");
            }

            // Decide which outline to use. Keep the one with thickness > 0
            // (more priority to generic configuration than page settings).
            const itemOutline = itemDesc.Outline;
            const outline = isDefined(itemOutline) && itemOutline.Thickness > 0
                ? itemDesc.Outline : elSettings.Outline;

            // We user the box shadow because the
            // outline prop doesn't support radius.
            if (isDefined(outline) && outline.Thickness > 0) {
                style.boxShadow = '0 0 0 '
                    + outline.Thickness + 'px '
                    + outline.Color;
            }

            return <div
                id={"widget-" + this.props.ItemDesc.Key}
                key={this.randomKey}
                className="noselect"
                style={style}>
                {widgetTitle}
                <div key="body" style={{ width: width, height: height }}>
                    {itemDesc.Render(itemDesc.Key, width, height)}
                </div>
            </div>;
        }
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================